import type { Dispatch, SetStateAction } from "react";
import { TIER_KEYS, type TierKey } from "@reach/core";
import {
  DeleteTopicModal,
  HolidayModal,
  ImportResultsModal,
  PlanConfirmModal,
  ProviderModal,
  TrainingModal,
  WipeAnalyticsModal,
  WipeBrainLibraryModal,
} from "../modals/Modals";
import type { KbItem } from "../types";
import type {
  SettingsBillingGroup,
  SettingsHolidayGroup,
  SettingsKbGroup,
  SettingsModalsGroup,
  SettingsTrainingGroup,
  SettingsUiGroup,
} from "../hooks/useSettingsPageState";

type SettingsModalStackProps = {
  // State groups
  kb: SettingsKbGroup;
  training: SettingsTrainingGroup;
  billing: SettingsBillingGroup;
  modals: SettingsModalsGroup;
  ui: Pick<SettingsUiGroup, "importResults">;
  holiday: SettingsHolidayGroup;
  // Misc state not captured by groups
  analyticsWipeLoading: boolean;
  wipeConfirmText: string;
  setWipeConfirmText: Dispatch<SetStateAction<string>>;
  loading: boolean;
  filteredKbItems: KbItem[];
  // Config / derived
  currentTier: TierKey;
  shopDomain: string;
  showPhase2Tiers: boolean;
  stripeLogo: string;
  lemonSqueezyLogo: string;
  // Handlers
  closeTrainingModal: () => void;
  onCommitPlanChange: (planId: TierKey) => void | Promise<void>;
  handleDeleteTopicConfirm: () => void | Promise<void>;
  handleExportCSV: (items: KbItem[]) => void;
  handleExportJSON: (items: KbItem[]) => void;
  handleHolidayAddOrUpdate: () => void;
  handleHolidaySave: () => void;
  handleMonthChange: (month: number, year: number) => void;
  handleOpenDuplicateTopic: () => void;
  handleProviderContinue: () => void | Promise<void>;
  handleTrainingSave: () => void | Promise<void>;
  handleWipeAnalytics: () => void | Promise<void>;
  handleWipeKb: () => void | Promise<void>;
  resetHolidayModal: () => void;
  resetProviderModal: () => void;
};

export function SettingsModalStack({
  kb,
  training,
  billing,
  modals,
  ui,
  holiday,
  analyticsWipeLoading,
  wipeConfirmText,
  setWipeConfirmText,
  loading,
  filteredKbItems,
  currentTier,
  shopDomain,
  showPhase2Tiers,
  stripeLogo,
  lemonSqueezyLogo,
  closeTrainingModal,
  onCommitPlanChange,
  handleDeleteTopicConfirm,
  handleExportCSV,
  handleExportJSON,
  handleHolidayAddOrUpdate,
  handleHolidaySave,
  handleMonthChange,
  handleOpenDuplicateTopic,
  handleProviderContinue,
  handleTrainingSave,
  handleWipeAnalytics,
  handleWipeKb,
  resetHolidayModal,
  resetProviderModal,
}: SettingsModalStackProps) {
  return (
    <>
      <PlanConfirmModal
        open={modals.isModalOpen}
        currentTier={currentTier}
        pendingPlan={billing.pendingPlan}
        tierKeys={TIER_KEYS}
        loading={loading}
        onClose={() => modals.setIsModalOpen(false)}
        onConfirm={onCommitPlanChange}
      />

      <ProviderModal
        open={modals.showProviderModal}
        pendingProviderTier={billing.pendingProviderTier}
        selectedProvider={billing.selectedProvider}
        rememberProvider={billing.rememberProvider}
        showPromoField={billing.showPromoField}
        promoCode={billing.promoCode}
        loading={loading}
        stripeLogo={stripeLogo}
        lemonSqueezyLogo={lemonSqueezyLogo}
        onClose={resetProviderModal}
        onContinue={handleProviderContinue}
        setSelectedProvider={billing.setSelectedProvider}
        setRememberProvider={billing.setRememberProvider}
        setShowPromoField={billing.setShowPromoField}
        setPromoCode={billing.setPromoCode}
      />

      <WipeBrainLibraryModal
        open={modals.showWipeModal}
        confirmText={kb.kbWipeConfirmText}
        shopDomain={shopDomain}
        loading={kb.kbDeleteLoading}
        onClose={() => { modals.setShowWipeModal(false); kb.setKbWipeConfirmText(""); }}
        onConfirm={handleWipeKb}
        onConfirmTextChange={kb.setKbWipeConfirmText}
        onExportJSON={() => handleExportJSON(filteredKbItems)}
        onExportCSV={() => handleExportCSV(filteredKbItems)}
      />

      <WipeAnalyticsModal
        open={modals.showAnalyticsWipeModal}
        confirmText={wipeConfirmText}
        shopDomain={shopDomain}
        loading={analyticsWipeLoading}
        onClose={() => { modals.setShowAnalyticsWipeModal(false); setWipeConfirmText(""); }}
        onConfirm={handleWipeAnalytics}
        onConfirmTextChange={setWipeConfirmText}
      />

      <DeleteTopicModal
        open={modals.showDeleteTopicModal}
        loading={kb.kbDeleteLoading}
        onClose={() => modals.setShowDeleteTopicModal(false)}
        onConfirm={handleDeleteTopicConfirm}
      />

      <ImportResultsModal
        open={modals.showImportResultsModal}
        total={ui.importResults.total}
        inserted={ui.importResults.inserted}
        updated={ui.importResults.updated}
        skipped={ui.importResults.skipped}
        onClose={() => modals.setShowImportResultsModal(false)}
      />

      <HolidayModal
        open={modals.showHolidayModal}
        editingHolidayId={holiday.editingHolidayId}
        hName={holiday.hName}
        localHolidays={holiday.localHolidays}
        month={holiday.month}
        year={holiday.year}
        selectedDates={holiday.selectedDates}
        onAddOrUpdate={handleHolidayAddOrUpdate}
        onChangeEditingHolidayId={holiday.setEditingHolidayId}
        onChangeHName={holiday.setHName}
        onChangeLocalHolidays={holiday.setLocalHolidays}
        onChangeMonth={handleMonthChange}
        onChangeSelectedDates={holiday.setSelectedDates}
        onClose={resetHolidayModal}
        onDeleteStringHoliday={(index) => holiday.setLocalHolidays((prev) => prev.filter((_, i) => i !== index))}
        onEditHoliday={(h) => {
          holiday.setEditingHolidayId(h.id);
          holiday.setHName(h.name);
          holiday.setSelectedDates({ start: new Date(h.startDate), end: new Date(h.endDate) });
        }}
        onSave={handleHolidaySave}
      />

      <TrainingModal
        open={training.isTrainingModalOpen}
        selectedKbId={kb.selectedKbId}
        qQuestion={training.qQuestion}
        qCustomReply={training.qCustomReply}
        qKeywords={training.qKeywords}
        qSuggestions={training.qSuggestions}
        qTargetUrl={training.qTargetUrl}
        qContent={training.qContent}
        duplicateTopic={training.duplicateTopic}
        isReadOnly={training.isTrainingReadOnly}
        showPhase2Tiers={showPhase2Tiers}
        onClose={closeTrainingModal}
        onEditDuplicateTopic={handleOpenDuplicateTopic}
        onEnableEdit={() => training.setIsTrainingReadOnly(false)}
        onSave={handleTrainingSave}
        setQQuestion={training.setQQuestion}
        setQCustomReply={training.setQCustomReply}
        setQKeywords={training.setQKeywords}
        setQSuggestions={training.setQSuggestions}
        setQTargetUrl={training.setQTargetUrl}
        setQContent={training.setQContent}
      />
    </>
  );
}
